import { Box, Button, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { ConnectedDevice } from '@synergetx/puppet-interview-types';
import {
    DEVICES_COLUMNS,
    DEVICES_COLUMN_VISIBILITY,
    DEVICES_EXPORT_COLUMNS,
    DEVICES_EXPORT_FORMATTERS,
} from 'constants/devicesTableColumns';
import { getDevices } from '@modules/firebase/database';
import DataGrid from '@components/DataGrid';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { exportToCsv } from '@utils/exportCsv';
import DownloadIcon from '@mui/icons-material/Download';

function DevicesTable() {
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;
    const interviewId = selectedInterview?.id;

    const [devices, setDevices] = useState<ConnectedDevice[] | null>();

    useEffect(() => {
        // clear out any existing devices to show loading state when switching Interviews
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDevices(null);

        if (!groupId || !interviewId) return;

        const unsubscribe = getDevices(interviewId, groupId, setDevices);

        return () => unsubscribe();
    }, [groupId, interviewId]);

    const handleExportCsv = useCallback(() => {
        if (!devices?.length) return;

        exportToCsv(DEVICES_COLUMNS, devices, `Devices ${new Date().toLocaleString()}`, {
            includeColumns: DEVICES_EXPORT_COLUMNS,
            formatters: DEVICES_EXPORT_FORMATTERS,
        });
    }, [devices]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0, // Allow flex shrinking
            }}
        >
            <DataGrid
                columns={DEVICES_COLUMNS}
                rows={devices ?? []}
                loading={devices == null}
                getRowId={(row) => row.info?.installationId ?? row.info?.deviceName ?? ''}
                columnVisibilityModel={DEVICES_COLUMN_VISIBILITY}
                toolbar={
                    <>
                        <Typography variant="h5" sx={{ mr: 2 }}>
                            Devices
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportCsv}
                            disabled={!devices?.length}
                        >
                            Export CSV
                        </Button>
                    </>
                }
                emptyMessage={
                    devices?.length
                        ? 'No Items Found'
                        : 'No devices yet. Have you installed the app and selected the correct survey?'
                }
            />
        </Box>
    );
}

export default memo(DevicesTable);
